import { useRef, useState } from "react";
import api from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import { toast } from "sonner";
import { UploadSimple, X, Image as ImageIcon } from "@phosphor-icons/react";

/**
 * ImageUpload — uploads to /api/uploads/image, returns data URL.
 *
 * Props:
 *   value: current url (single) — pass null/"" if empty
 *   onChange: (url) => void
 *   aspect: tailwind class for aspect ratio (default "aspect-video")
 *   label: optional label
 *   testId: data-testid prefix
 */
export function ImageUpload({ value, onChange, aspect = "aspect-video", label, testId = "img" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Image only"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/uploads/image", fd);
      onChange(data.url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div>
      {label && <label className="text-[10px] tracking-luxe uppercase text-white/60">{label}</label>}
      <div className={`mt-1.5 ${aspect} relative rounded-md border border-dashed border-white/15 bg-[#0a0a0a] overflow-hidden flex items-center justify-center`} data-testid={`${testId}-box`}>
        {value ? (
          <>
            <img src={value} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange("")} className="absolute top-2 right-2 bg-black/70 border border-white/20 rounded-full p-1.5 text-white/80 hover:text-white" data-testid={`${testId}-remove`}>
              <X size={14} />
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center text-white/40 px-4 text-center">
            <ImageIcon size={28} weight="duotone" />
            <p className="text-xs mt-2">No image yet</p>
          </div>
        )}
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 bg-black/70 border border-[#D4AF37]/40 rounded-full px-3 py-1.5 text-[10px] tracking-luxe uppercase text-[#D4AF37] hover:bg-[#D4AF37]/10"
          data-testid={`${testId}-pick`}>
          <UploadSimple size={12} /> {busy ? "Uploading…" : value ? "Replace" : "Upload"}
        </button>
        <input ref={inputRef} type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0])} className="hidden" data-testid={`${testId}-input`} />
      </div>
    </div>
  );
}

/**
 * GalleryUpload — list of images, click to add more, remove individually.
 */
export function GalleryUpload({ value = [], onChange, testId = "gallery" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of Array.from(files)) {
        if (!f.type?.startsWith("image/")) continue;
        if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name}: max 5MB`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads/image", fd);
        urls.push(data.url);
      }
      if (urls.length) onChange([...value, ...urls]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-[10px] tracking-luxe uppercase text-white/60">Gallery photos</label>
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          className="inline-flex items-center gap-1.5 text-[10px] tracking-luxe uppercase text-[#D4AF37] hover:text-[#F9E596]"
          data-testid={`${testId}-add`}>
          <UploadSimple size={12} /> {busy ? "Uploading…" : "Add photos"}
        </button>
      </div>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {value.map((url, i) => (
          <div key={i} className="aspect-square relative rounded-md overflow-hidden border border-white/10 bg-[#0a0a0a]" data-testid={`${testId}-item-${i}`}>
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => remove(i)} className="absolute top-1.5 right-1.5 bg-black/70 border border-white/20 rounded-full p-1 text-white/80 hover:text-white" data-testid={`${testId}-remove-${i}`}>
              <X size={12} />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          className="aspect-square rounded-md border border-dashed border-white/15 bg-[#0a0a0a] flex flex-col items-center justify-center text-white/40 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-colors"
          data-testid={`${testId}-empty-add`}>
          <UploadSimple size={20} />
          <span className="text-[10px] tracking-luxe uppercase mt-1">{busy ? "…" : "Add"}</span>
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple onChange={(e) => upload(e.target.files)} className="hidden" data-testid={`${testId}-input`} />
    </div>
  );
}
